import router from '@router'
import store from '@state/store'

export const WALLET_NAVIGATION_QUERY_KEY = 'walletNavigation'

export const navigateToWallet = (options = {}) => {
  const navigationState = buildNavigationState(options)
  if (!navigationState.routeName) return

  openNavigationRoute({
    navigationState,
    query: buildNavigationQuery({ navigationState }),
  })
}

export const consumeWalletNavigationPayload = ({ route } = {}) => {
  const navigationState = parseNavigationStateFromRoute({ route })
  const routeName = route && route.name ? route.name : ''

  if (!isRouteMatch({ navigationState, routeName })) return null

  const payload = normalizeNavigationPayload({ navigationState })
  if (!payload) return null

  applyClientSelection({ client: payload.client })
  return payload
}

export const clearWalletNavigationQuery = ({ route, router } = {}) => {
  clearNavigationQuery({ route, router: router || router })
}

const buildNavigationState = ({
  clientData,
  clientId,
  routeName = '',
  openInNewTab = true,
  filters,
} = {}) => {
  return {
    routeName,
    openInNewTab: Boolean(openInNewTab),
    filters: filters || {},
    client: resolveClientData({ clientData, clientId }),
  }
}

const openNavigationRoute = ({ navigationState, query }) => {
  const hrefValue = resolveNavigationHref({ navigationState, query })
  if (navigationState.openInNewTab && hrefValue) {
    window.open(hrefValue, '_blank')
    return
  }
  router.push({ name: navigationState.routeName, query: query || {} })
}

const resolveClientData = ({ clientData, clientId }) => {
  if (clientData) return buildClientPayload({ clientData, clientId })
  if (!clientId) return null

  const clientFound = findClientById({
    clientList: store.getters['user/allClients'] || [],
    clientId,
  })

  const resolved = clientFound || { id: clientId }
  return buildClientPayload({ clientData: resolved, clientId })
}

export const findClientById = ({ clientList, clientId }) => {
  const safeList = Array.isArray(clientList) ? clientList : []
  return safeList.find((clientItem) => {
    const clientIdent = resolveClientIdentifier({ clientItem })
    return String(clientIdent) === String(clientId)
  })
}

const resolveClientIdentifier = ({ clientItem }) => {
  if (!clientItem) return ''

  return (
    clientItem.id ||
    clientItem.id_cliente ||
    clientItem.id_cadastro_cliente ||
    ''
  )
}

const buildClientPayload = ({ clientData, clientId }) => {
  const safeClient = clientData || {}
  const clientIdent =
    resolveClientIdentifier({ clientItem: safeClient }) || clientId || ''

  return {
    id: clientIdent,
    id_cadastro_cliente:
      safeClient.id_cadastro_cliente || safeClient.id_cadastro || '',
    name: safeClient.nome_cliente || safeClient.name || safeClient.nome || '',
  }
}

const buildNavigationQuery = ({ navigationState }) => {
  if (!navigationState) return {}

  const payload = buildNavigationPayload({ navigationState })
  if (!payload) return {}

  return {
    [WALLET_NAVIGATION_QUERY_KEY]: JSON.stringify(payload),
  }
}

const buildNavigationPayload = ({ navigationState }) => {
  const hasFilters = hasObjectKeys({ source: navigationState.filters })
  if (!navigationState.client && !hasFilters) return null

  return {
    routeName: navigationState.routeName,
    client: navigationState.client || null,
    filters: navigationState.filters || {},
  }
}

const parseNavigationStateFromRoute = ({ route }) => {
  const query = route && route.query ? route.query : {}
  const rawValue = query[WALLET_NAVIGATION_QUERY_KEY]
  if (!rawValue) return null
  return parseNavigationValue({ rawValue })
}

const parseNavigationValue = ({ rawValue }) => {
  const serializedValue = Array.isArray(rawValue) ? rawValue[0] : rawValue
  if (!serializedValue || typeof serializedValue !== 'string') return null

  try {
    const parsed = JSON.parse(serializedValue)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch (error) {
    return null
  }
}

const isRouteMatch = ({ navigationState, routeName }) => {
  if (!navigationState || !navigationState.routeName) return false
  if (!routeName) return false
  return navigationState.routeName === routeName
}

const normalizeNavigationPayload = ({ navigationState }) => {
  if (!navigationState) return null

  const filters = resolveFiltersPayload({ navigationState })
  if (!filters) return null

  return {
    routeName: navigationState.routeName,
    client: navigationState.client || null,
    filters,
  }
}

const isValidFiltersPayload = ({ filters }) => {
  if (filters === null || typeof filters !== 'object') return false
  if (Array.isArray(filters)) return false
  return true
}

const resolveFiltersPayload = ({ navigationState }) => {
  const filtersValue = navigationState ? navigationState.filters : undefined
  if (typeof filtersValue === 'undefined') return {}
  if (!isValidFiltersPayload({ filters: filtersValue })) return null
  return filtersValue
}

const applyClientSelection = ({ client }) => {
  if (!client) return
  store.dispatch('user/addSearchClient', client)
}

const resolveNavigationHref = ({ navigationState, query }) => {
  const routeData = router.resolve({
    name: navigationState.routeName,
    query: query || {},
  })
  return routeData && routeData.href ? routeData.href : ''
}

const clearNavigationQuery = ({ route, router }) => {
  if (!route) return

  const query = { ...(route.query || {}) }
  if (!query[WALLET_NAVIGATION_QUERY_KEY]) return

  delete query[WALLET_NAVIGATION_QUERY_KEY]

  replaceUrlState({
    href: resolveNavigationHrefFromRoute({ route, router, query }),
  })
}

const hasObjectKeys = ({ source }) => {
  if (!source || typeof source !== 'object') return false
  if (Array.isArray(source)) return false
  return Object.keys(source).length > 0
}

const resolveNavigationHrefFromRoute = ({ route, router, query }) => {
  const routeData = router.resolve({
    name: route.name,
    params: route.params,
    query: query || {},
  })
  return routeData && routeData.href ? routeData.href : ''
}

const replaceUrlState = ({ href }) => {
  if (!href) return
  window.history.replaceState({}, '', href)
}
